import ExpandMore from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import { capitalize } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  ASSISTANT_PLANS,
  CLUSTER_TYPES,
  INSTANCE_CATEGORY,
} from '../../../constants';
import {
  isAssistantInstance,
  isCustomInstance,
  isModelInstance,
  isSolrCloud,
  isSolrInstance,
} from '../../../utils/utils';
import Text from '../../elements/text/Text';

const MoreInstanceInfo = ({ instance }) => {
  const { t } = useTranslation();

  const instanceTypes = {
    [CLUSTER_TYPES.SOLR_CLUSTER]: t('solrInstance', { ns: 'instance' }),
    [CLUSTER_TYPES.GPS_CLUSTER]: t('gpsInstance', { ns: 'gps' }),
    [CLUSTER_TYPES.MODEL_CLUSTER]: t('models', { ns: 'model' }),
    [CLUSTER_TYPES.CUSTOM_CLUSTER]: t('customInst', { ns: 'customInst' }),
    [CLUSTER_TYPES.ASSISTANT_CLUSTER]: t('instance', { ns: 'assistant' }),
  };

  return (
    <Accordion variant="outlined">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Text sx={{ fontWeight: 600 }}>
            {t('settings.additional', { ns: 'sectionTitles' })}
          </Text>
          <Text sx={{ fontWeight: 600 }}>
            {t('clickMore', { ns: 'general' })}
          </Text>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          <Grid size={3}>
            <Text sx={{ fontWeight: 600 }}>
              {t('instanceType', { ns: 'fields' })}
            </Text>
          </Grid>
          <Grid size={9}>{instanceTypes[instance.clusterType]}</Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          {isSolrInstance(instance.clusterType) && (
            <>
              <Grid size={3}>
                <Text sx={{ fontWeight: 600 }}>
                  {t('instancePlan', { ns: 'fields' })}
                </Text>
              </Grid>
              <Grid size={9}>
                {instance.category === INSTANCE_CATEGORY.COMMUNITY
                  ? capitalize(
                      t('plan', {
                        ns: 'instance',
                        type: instance.category,
                      })
                    )
                  : instance.category === INSTANCE_CATEGORY.ENTERPRISE
                    ? capitalize(
                        t('plan', {
                          ns: 'instance',
                          type: instance.category,
                        })
                      )
                    : `${t('standard', { ns: 'instance' })} - ${capitalize(
                        t('plan', {
                          ns: 'instance',
                          type: t(instance.instances[0].type.toLowerCase(), {
                            ns: 'instance',
                          }),
                        })
                      )}`}
              </Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={3}>
                <Text sx={{ fontWeight: 600 }}>
                  {t('solrType', { ns: 'instance' })}
                </Text>
              </Grid>
              <Grid size={9}>
                {isSolrCloud(instance.type)
                  ? t('solrCloudType', { ns: 'instance' })
                  : t('standaloneType', { ns: 'instance' })}
              </Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={3}>
                <Text sx={{ fontWeight: 600 }}>
                  {t('solrVersion', { ns: 'fields' })}
                </Text>
              </Grid>
              <Grid size={9}>{instance.solrVersion}</Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
            </>
          )}
          {isModelInstance(instance.clusterType) && (
            <>
              <Grid size={3}>
                <Text sx={{ fontWeight: 600 }}>
                  {t('modelType', { ns: 'model' })}
                </Text>
              </Grid>
              <Grid size={9}>{instance.category}</Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
            </>
          )}
          {isCustomInstance(instance.clusterType) && (
            <>
              <Grid size={3}>
                <Text sx={{ fontWeight: 600 }}>
                  {t('instanceDesignation', { ns: 'customInst' })}
                </Text>
              </Grid>
              <Grid size={9}>{instance.designation}</Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
            </>
          )}
          {isAssistantInstance(instance.clusterType) && (
            <>
              <Grid size={3}>
                <Text sx={{ fontWeight: 600 }}>
                  {t('plan', { ns: 'assistant' })}
                </Text>
              </Grid>
              <Grid size={9}>
                {t('plan', {
                  ns: 'instance',
                  type: ASSISTANT_PLANS[instance.instances[0].type],
                })}
              </Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
            </>
          )}
          <Grid size={3}>
            <Text sx={{ fontWeight: 600 }}>
              {t('storageSize', { ns: 'instance' })}
            </Text>
          </Grid>
          <Grid size={9}>{instance.instances[0].storageSize} GB</Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

MoreInstanceInfo.propTypes = {
  instance: PropTypes.object.isRequired,
};

export default MoreInstanceInfo;
